import { useId, useState } from 'react';
import { Button, Field, Input, Select } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';
import { toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import type { PriceList, ProductDetail, ProductPrices } from '../lib/types';

/**
 * Adet bazlı (kademeli) fiyatlar (F5B-11, E-23): bir listede "N adet ve üzeri" fiyatı. Sepette adede uyan en yüksek
 * eşik uygulanır (fiyat çözümleme, ADR-020). 1 adet fiyatı üstteki tabloda girilir.
 */
export function PriceTiers({
  product,
  lists,
  prices,
  canWrite,
}: {
  product: ProductDetail;
  lists: PriceList[];
  prices: ProductPrices;
  canWrite: boolean;
}) {
  const t = useT();
  const money = useMoney();
  const titleId = useId();
  const variants = product.variants.filter((v) => v.status === 'active');
  const [f, setF] = useState({ priceListId: '', variantId: '', minQuantity: '', amount: '' });
  const listId = f.priceListId || lists[0]?.id || '';
  const variantId = f.variantId || variants[0]?.id || '';
  const list = lists.find((l) => l.id === listId);
  const tiers = prices
    .filter((p) => p.minQuantity > 1)
    .sort((a, b) => a.priceListId.localeCompare(b.priceListId) || a.minQuantity - b.minQuantity);
  const save = useApiMutation(
    (v: { priceListId: string; variantId: string; minQuantity: number; amountMinor: number | null }) =>
      api.put('/v1/admin/prices', v),
    {
      invalidate: [['product-prices', product.id]],
      success: t('common.saved'),
      onSuccess: (_r, v) => {
        if (v.amountMinor !== null) setF({ ...f, minQuantity: '', amount: '' });
      },
      silentFieldErrors: true,
    },
  );
  const qty = /^\d+$/.test(f.minQuantity.trim()) ? Number(f.minQuantity.trim()) : null;
  const amount = list ? toMinor(f.amount, list.currency) : null;
  return (
    <div className="mt-5" role="group" aria-labelledby={titleId}>
      <div className="flex items-center gap-1">
        <h3 id={titleId} className="text-sm font-medium">
          {t('admin.priceTiers.title')}
        </h3>
        <HelpTip k="products.price_tiers" />
      </div>
      <p className="mt-0.5 text-sm text-ink-3">{t('admin.priceTiers.lead')}</p>
      {tiers.length > 0 ? (
        <ul className="mt-2 divide-y divide-line rounded-md border border-line text-sm">
          {tiers.map((p) => {
            const l = lists.find((x) => x.id === p.priceListId);
            const v = variants.find((x) => x.id === p.variantId);
            return (
              <li
                key={`${p.priceListId}:${p.variantId}:${p.minQuantity}`}
                className="flex flex-wrap items-center gap-3 px-3 py-2"
              >
                <span className="min-w-0 flex-1">
                  {t('admin.priceTiers.row', {
                    list: l?.name ?? '—',
                    sku: v?.sku ?? '—',
                    qty: p.minQuantity,
                    price: money(p.amountMinor, l?.currency ?? null),
                  })}
                </span>
                {canWrite && (
                  <ConfirmButton
                    variant="ghost"
                    message={t('admin.priceTiers.removeConfirm', { qty: p.minQuantity })}
                    onConfirm={() =>
                      save.mutateAsync({
                        priceListId: p.priceListId,
                        variantId: p.variantId,
                        minQuantity: p.minQuantity,
                        amountMinor: null,
                      })
                    }
                  >
                    {t('admin.priceTiers.remove')}
                  </ConfirmButton>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-ink-3">{t('admin.priceTiers.empty')}</p>
      )}
      {canWrite && variants.length > 0 && lists.length > 0 && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <Field label={t('admin.priceTiers.list')} className="w-48">
            {(p) => (
              <Select {...p} value={listId} onChange={(e) => setF({ ...f, priceListId: e.target.value })}>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {variants.length > 1 && (
            <Field label={t('admin.priceTiers.variant')} className="w-44">
              {(p) => (
                <Select {...p} value={variantId} onChange={(e) => setF({ ...f, variantId: e.target.value })}>
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.sku}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <Field
            label={t('admin.priceTiers.minQuantity')}
            className="w-32"
            error={fieldError(save.error, 'minQuantity')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="numeric"
                value={f.minQuantity}
                onChange={(e) => setF({ ...f, minQuantity: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('admin.priceTiers.price', { currency: list?.currency ?? '' })} className="w-36">
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                className="tabular"
                value={f.amount}
                onChange={(e) => setF({ ...f, amount: e.target.value })}
              />
            )}
          </Field>
          <Button
            loading={save.isPending}
            disabled={!qty || qty < 2 || amount === null || amount < 0}
            onClick={() =>
              save.mutate({ priceListId: listId, variantId, minQuantity: qty!, amountMinor: amount })
            }
          >
            {t('admin.priceTiers.add')}
          </Button>
        </div>
      )}
    </div>
  );
}
